import type { BlogPost } from "@/types/content";

// Sports injury articles (posts 33-40).
// Article images are managed centrally in src/data/blog/images.ts (keyed by slug).
export const sportsPosts: BlogPost[] = [
  {
    id: 33,
    slug: "common-sports-injuries-and-their-prevention",
    title: "Common Sports Injuries and Their Prevention",
    category: "Sports Injuries",
    excerpt: "Most sports injuries involve ligaments, tendons and muscles. Many can be prevented with smart habits.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20illustration%20of%20the%20knee%20and%20ankle%20ligaments%20beside%20clean%20sports%20safety%20equipment%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-33&orientation=landscape&nocache=true",
    imageAlt: "Athletes training as part of sports injury prevention",
    metaTitle: "Common Sports Injuries and Prevention | Ortho Spine Care",
    metaDescription:
      "Learn about common sports injuries and the practical steps that help prevent them. Patient-friendly guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-14",
    updatedDate: "2026-06-15",
    readTime: "5 min read",
    tags: ["Sports Injuries", "Prevention", "Ligaments"],
    content: [
      {
        heading: "The injuries we see most often",
        bullets: [
          "Ligament injuries around the knee and ankle",
          "Tendon injuries such as rotator cuff problems and heel tendon injuries",
          "Muscle strains, particularly of the thigh and calf",
          "Overuse injuries from repetitive activity",
        ],
      },
      {
        heading: "How to reduce your risk",
        bullets: [
          "Warm up properly before activity",
          "Increase training load gradually",
          "Use appropriate footwear and equipment",
          "Include strength and balance work in your routine",
          "Allow adequate recovery between sessions",
        ],
      },
      {
        heading: "If an injury happens",
        paragraphs: [
          "Rest, cold packs, compression and elevation help in the first 48 hours. If there is swelling, instability or difficulty bearing weight, an early assessment helps determine the extent of the injury.",
        ],
      },
    ],
    faqs: [
      {
        q: "Should I play through pain?",
        a: "Sharp or worsening pain is a signal to stop. Continuing to train through significant pain often turns a minor injury into a bigger one.",
      },
      {
        q: "When can I return to sport?",
        a: "Return depends on the injury and how rehabilitation progresses. Your clinician and physiotherapist will guide a safe return.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 34,
    slug: "how-to-prevent-sports-related-joint-injuries",
    title: "How to Prevent Sports-Related Joint Injuries",
    category: "Sports Injuries",
    excerpt: "Joints take the load in sport. Protecting them is mostly about preparation and progression.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Balance%20and%20strength%20training%20equipment%20including%20a%20stability%20ball%20and%20resistance%20bands%20on%20a%20clean%20studio%20floor%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-34&orientation=landscape&nocache=true",
    imageAlt: "Balance and strength training to prevent joint injuries",
    metaTitle: "Preventing Sports-Related Joint Injuries | Ortho Spine Care",
    metaDescription:
      "Practical strategies to help prevent joint injuries in sport, from warm-ups to training progression. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-21",
    updatedDate: "2026-06-16",
    readTime: "5 min read",
    tags: ["Sports Injuries", "Joint Health", "Prevention"],
    content: [
      {
        heading: "Why joint injuries happen",
        paragraphs: [
          "Joints are injured by sudden twists, impacts, or by accumulating load faster than the body can adapt. Most prevention focuses on preparing the joint and building load sensibly.",
        ],
      },
      {
        heading: "Strategies that help",
        bullets: [
          "Warm up with dynamic movement rather than static stretching alone",
          "Build strength around the joint, especially the knee and shoulder",
          "Train balance and control to improve stability",
          "Progress training volume gradually week on week",
          "Do not return to full training too quickly after time off",
        ],
      },
      {
        heading: "Warning signs to respect",
        paragraphs: [
          "Persistent joint pain, swelling after activity, or a joint that feels unstable should not be ignored. Early assessment can prevent a small problem becoming a serious one.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do knee braces prevent injuries?",
        a: "Braces may provide support and confidence in some situations, but they are not a substitute for strength, technique and sensible training.",
      },
      {
        q: "Is stretching before sport useful?",
        a: "Dynamic warm-up involving movement is generally more effective before activity. Static stretching is better suited after training.",
      },
    ],
    relatedServices: ["joint-disorders", "tendon-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 35,
    slug: "sports-injury-when-should-you-see-an-orthopaedic-specialist",
    title: "Sports Injury: When Should You See an Orthopaedic Specialist?",
    category: "Sports Injuries",
    excerpt: "Most minor knocks settle. These signs mean a sports injury deserves a specialist look.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20knee%20joint%20with%20ligaments%20highlighted%20after%20injury%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-35&orientation=landscape&nocache=true",
    imageAlt: "Specialist assessing an athlete's knee injury",
    metaTitle: "Sports Injury: When to See a Specialist | Ortho Spine Care",
    metaDescription:
      "Learn which sports injury signs need an orthopaedic specialist and what to expect from assessment. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-01-28",
    updatedDate: "2026-06-18",
    readTime: "4 min read",
    tags: ["Sports Injuries", "Patient Education", "Diagnosis"],
    content: [
      {
        heading: "Signs that need assessment",
        bullets: [
          "Inability to bear weight on the injured limb",
          "Rapid or significant swelling",
          "The joint giving way or feeling unstable",
          "A joint that locks or cannot fully straighten",
          "Pain that does not improve over several days",
        ],
      },
      {
        heading: "Why the first assessment matters",
        paragraphs: [
          "Getting the diagnosis right early helps prevent the injury from becoming chronic. It also guides the correct rehabilitation so you can return to sport safely.",
        ],
      },
      {
        heading: "Treatment options",
        paragraphs: [
          "Many sports injuries are treated with rest, physiotherapy and a graded return to activity. When a ligament or tendon requires repair, arthroscopic or open surgery may be recommended.",
        ],
      },
    ],
    faqs: [
      {
        q: "Do I need an MRI for a sports injury?",
        a: "Not always. Imaging is arranged when it is likely to change the diagnosis or treatment, such as when a ligament or tendon injury is suspected.",
      },
      {
        q: "Can I keep training with a minor injury?",
        a: "It depends on the injury. Relative rest and modified training often help, but significant pain or instability should be assessed first.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 36,
    slug: "muscle-and-ligament-injuries-explained",
    title: "Muscle and Ligament Injuries Explained",
    category: "Sports Injuries",
    excerpt: "Muscles and ligaments are injured in different ways — and their recovery differs too.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20illustration%20of%20the%20calf%20muscle%20and%20ankle%20ligaments%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-36&orientation=landscape&nocache=true",
    imageAlt: "Supported muscle injury on an athlete's calf",
    metaTitle: "Muscle and Ligament Injuries Explained | Ortho Spine Care",
    metaDescription:
      "Understand the difference between muscle strains and ligament injuries, their symptoms and treatment. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-04",
    updatedDate: "2026-06-19",
    readTime: "5 min read",
    tags: ["Sports Injuries", "Ligaments", "Muscles"],
    content: [
      {
        heading: "Muscle strains",
        paragraphs: [
          "A muscle strain is a tear in the muscle fibres, usually from a sudden stretch or contraction. Symptoms include pain, tightness and sometimes bruising. Most strains heal with rest and graded rehabilitation.",
        ],
      },
      {
        heading: "Ligament injuries",
        paragraphs: [
          "Ligaments connect bone to bone and stabilize joints. A sprain is a stretch or tear of a ligament, often from a twist. Swelling, pain and instability are common, and more severe tears may need surgery.",
        ],
      },
      {
        heading: "Why the distinction matters",
        bullets: [
          "Muscle injuries usually respond well to graded loading",
          "Ligament injuries may need assessment of joint stability",
          "Both benefit from structured rehabilitation",
          "Return to sport should be guided, not rushed",
        ],
      },
    ],
    faqs: [
      {
        q: "What is the difference between a sprain and a strain?",
        a: "A sprain involves a ligament, while a strain involves a muscle or tendon. Both can cause pain and swelling but are treated differently.",
      },
      {
        q: "Should I stretch a strained muscle?",
        a: "Avoid aggressive stretching in the early phase. Gentle, guided movement and progressive strengthening are usually more helpful.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 37,
    slug: "recovery-after-a-sports-injury",
    title: "Recovery After a Sports Injury",
    category: "Sports Injuries",
    excerpt: "Recovery is more than waiting for pain to settle. A structured approach gets you back safely.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Rehabilitation%20equipment%20including%20resistance%20bands%20and%20a%20balance%20pad%20on%20a%20clean%20light%20floor%20in%20a%20bright%20room%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-37&orientation=landscape&nocache=true",
    imageAlt: "Athlete performing guided rehabilitation exercises",
    metaTitle: "Recovery After a Sports Injury | Ortho Spine Care",
    metaDescription:
      "Learn what a good recovery programme looks like after a sports injury, from early care to returning to training. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-11",
    updatedDate: "2026-06-20",
    readTime: "5 min read",
    tags: ["Sports Injuries", "Rehabilitation", "Recovery"],
    content: [
      {
        heading: "Phase 1: Settle the injury",
        paragraphs: [
          "In the early phase, the focus is on controlling pain and swelling, protecting the injured area, and maintaining movement in the rest of the body where possible.",
        ],
      },
      {
        heading: "Phase 2: Restore movement and strength",
        bullets: [
          "Gentle range-of-motion exercise",
          "Progressive strengthening of the surrounding muscles",
          "Balance and control work",
          "Gradual increase in load under guidance",
        ],
      },
      {
        heading: "Phase 3: Return to sport",
        paragraphs: [
          "Return to sport is a step-by-step process, not a single decision. Sport-specific drills and confidence in the injured area come before full training and competition.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does sports injury recovery take?",
        a: "It varies widely with the type and severity of injury. Your clinician and physiotherapist will set realistic milestones.",
      },
      {
        q: "Can I speed up recovery?",
        a: "Following your rehabilitation plan and progressing at the right pace helps recovery go smoothly. Rushing often leads to setbacks.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 38,
    slug: "warm-up-and-stretching-for-injury-prevention",
    title: "Warm-Up and Stretching for Injury Prevention",
    category: "Sports Injuries",
    excerpt: "A good warm-up prepares the body for activity and reduces unnecessary strain on muscles and joints.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=A%20mobility%20and%20stretching%20setup%20with%20an%20exercise%20mat%20and%20a%20foam%20roller%20on%20a%20clean%20light%20floor%20with%20no%20people%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-38&orientation=landscape&nocache=true",
    imageAlt: "Dynamic warm-up before exercise",
    metaTitle: "Warm-Up and Stretching for Injury Prevention | Ortho Spine Care",
    metaDescription:
      "Learn how to warm up and stretch properly to help prevent injuries during sport and exercise. Guidance from Ortho Spine Care in Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-18",
    updatedDate: "2026-06-21",
    readTime: "4 min read",
    tags: ["Sports Injuries", "Prevention", "Exercise"],
    content: [
      {
        heading: "Why warm up",
        paragraphs: [
          "A warm-up raises muscle temperature, improves blood flow and prepares the nervous system for activity. This reduces the risk of strain and helps performance.",
        ],
      },
      {
        heading: "What a good warm-up includes",
        bullets: [
          "Five to ten minutes of light activity to raise the heart rate",
          "Dynamic movements that take joints through their range",
          "Sport-specific drills at low intensity",
          "Gradual increase towards training pace",
        ],
      },
      {
        heading: "Where stretching fits",
        paragraphs: [
          "Dynamic movement before activity prepares the body best. Static stretching, where a position is held, is generally more useful after training when muscles are warm.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long should a warm-up be?",
        a: "Around five to ten minutes of progressive activity is a practical guide for most recreational athletes.",
      },
      {
        q: "Is stretching necessary?",
        a: "Stretching is one part of a balanced routine. For performance and injury prevention, dynamic warm-up and strengthening are equally important.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 39,
    slug: "common-knee-injuries-in-sports",
    title: "Common Knee Injuries in Sports",
    category: "Sports Injuries",
    excerpt: "The knee is the most commonly injured joint in sport. Here is what to watch for.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20knee%20ligaments%20and%20meniscus%2C%20a%20clean%20sports%20injury%20education%20visual%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-39&orientation=landscape&nocache=true",
    imageAlt: "Athlete holding the knee after a sports injury",
    metaTitle: "Common Knee Injuries in Sports | Ortho Spine Care",
    metaDescription:
      "Learn about common knee injuries in sport, including ligament and meniscus injuries, and when treatment is needed. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-02-25",
    updatedDate: "2026-06-22",
    readTime: "5 min read",
    tags: ["Sports Injuries", "Knee Health", "Ligaments"],
    content: [
      {
        heading: "Injuries we see most often",
        bullets: [
          "Ligament injuries from a twisting movement",
          "Meniscus or cartilage tears",
          "Tendon injuries around the knee",
          "Dislocation of the kneecap",
        ],
      },
      {
        heading: "Symptoms to look for",
        bullets: [
          "A popping or tearing sensation at the time of injury",
          "Rapid swelling within hours",
          "The knee giving way when walking or turning",
          "Locking or difficulty straightening the knee",
        ],
      },
      {
        heading: "Treatment options",
        paragraphs: [
          "Treatment depends on which structure is injured. Many knee injuries are managed with rehabilitation, while ligament or meniscus tears that affect stability may be treated with arthroscopic surgery.",
        ],
      },
    ],
    faqs: [
      {
        q: "Why is the knee so commonly injured?",
        a: "The knee bears weight while allowing a large range of motion, and it relies on ligaments and muscles for stability. This makes it vulnerable during twisting and impact.",
      },
      {
        q: "Do all knee ligament injuries need surgery?",
        a: "No. Some heal with rehabilitation alone. Surgery is considered when instability affects function or when a return to high-demand sport is planned.",
      },
    ],
    relatedServices: ["joint-disorders", "tendon-disorders"],
    relatedConditions: ["sports-injuries"],
  },
  {
    id: 40,
    slug: "common-shoulder-injuries-in-sports",
    title: "Common Shoulder Injuries in Sports",
    category: "Sports Injuries",
    excerpt: "Overhead and contact sports put the shoulder under strain. Here are the injuries to know about.",
    featuredImage:
      "https://readdy.ai/api/search-image?query=Detailed%20three%20dimensional%20anatomical%20render%20of%20the%20shoulder%20joint%2C%20rotator%20cuff%20and%20labrum%20on%20a%20clean%20light%20background%2C%20soft%20royal%20blue%2C%20cyan%20and%20violet%20accent%20lighting%2C%20clean%20premium%20healthcare%20aesthetic&width=800&height=600&seq=ospc-blog-40&orientation=landscape&nocache=true",
    imageAlt: "Athlete stretching the shoulder before overhead sport",
    metaTitle: "Common Shoulder Injuries in Sports | Ortho Spine Care",
    metaDescription:
      "Understand common shoulder injuries in sport, including rotator cuff problems and dislocations, and their treatment. Ortho Spine Care, Trichy.",
    author: "Dr. N. Ajit Kumar",
    publishedDate: "2026-03-04",
    updatedDate: "2026-06-23",
    readTime: "5 min read",
    tags: ["Sports Injuries", "Shoulder Health", "Tendon"],
    content: [
      {
        heading: "Common shoulder injuries",
        bullets: [
          "Rotator cuff tendon injuries",
          "Shoulder dislocation or instability",
          "Tendon inflammation from repetitive overhead use",
          "Labral injuries from throwing or contact",
        ],
      },
      {
        heading: "Symptoms",
        bullets: [
          "Pain when reaching overhead or behind the back",
          "Weakness when lifting the arm",
          "A feeling that the shoulder may slip out",
          "Night pain that disturbs sleep",
        ],
      },
      {
        heading: "Treatment options",
        paragraphs: [
          "Many shoulder injuries improve with rest, physiotherapy and a graded return to sport. Tendon or labral injuries that affect stability or function may need surgical repair.",
        ],
      },
    ],
    faqs: [
      {
        q: "How long does a shoulder injury take to heal?",
        a: "Recovery varies with the injury type and severity. Rehabilitation is usually measured in weeks to months rather than days.",
      },
      {
        q: "Should I keep training with shoulder pain?",
        a: "Avoid activities that sharply worsen the pain. Persistent or worsening shoulder pain should be assessed rather than trained through.",
      },
    ],
    relatedServices: ["tendon-disorders", "joint-disorders"],
    relatedConditions: ["sports-injuries"],
  },
];